import { useRef, useState, useEffect, useCallback } from "react";

// Add type definition for Turnstile
declare global {
  interface Window {
    turnstile: {
      render: (container: HTMLElement, options: any) => number;
      remove: (widgetId: number) => void;
      execute: (widgetId: number) => void;
    };
  }
}

interface RegisterResponse {
  success: boolean;
  userId?: string;
  error?: string;
}

export function useTurnstile(siteKey: string, action: string = "checkout") {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // Check for existing userId on component mount
  useEffect(() => {
    const existingUserId = localStorage.getItem("tritan-user-id");
    if (existingUserId) {
      setUserId(existingUserId);
    }
  }, []);

  // Dynamically load the Turnstile script if needed
  function loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.turnstile) {
        resolve();
        return;
      }
      const existing = document.querySelector('script[src*="turnstile"]');
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () =>
          reject("Failed to load Turnstile script")
        );
        return;
      }
      const script = document.createElement("script");
      script.src =
        "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject("Failed to load Turnstile script");
      document.head.appendChild(script);
    });
  }

  // Main function to call to get a token
  async function executeTurnstile(): Promise<string> {
    // If we already have a token, return it
    if (token) {
      return token;
    }

    setLoading(true);
    setError(null);

    try {
      await loadScript();

      const newToken = await new Promise<string>((resolve, reject) => {
        // Create a hidden container if needed
        if (!containerRef.current) {
          containerRef.current = document.createElement("div");
          containerRef.current.style.display = "none";
          document.body.appendChild(containerRef.current);
        }
        // Remove any previous widget
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
        // Render the widget
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          size: "invisible",
          callback: (turnstileToken: string) => {
            setToken(turnstileToken);
            resolve(turnstileToken);
          },
          "error-callback": () => {
            setError("Verification failed. Please try again.");
            reject("Verification failed. Please try again.");
          },
          "expired-callback": () => {
            setError("Verification expired. Please try again.");
            reject("Verification expired. Please try again.");
          },
          "refresh-expired": "auto",
        });
        // Execute the widget
        if (
          window.turnstile &&
          typeof (window.turnstile as any).execute === "function"
        ) {
          (window.turnstile as any).execute(widgetIdRef.current);
        } else {
          setError("Turnstile not ready");
          reject("Turnstile not ready");
        }
      });

      setLoading(false);
      return newToken;
    } catch (error) {
      setLoading(false);
      setError(error instanceof Error ? error.message : String(error));
      throw error;
    }
  }

  // Function to register a user with email and token
  const registerUser = useCallback(
    async (email: string, size: string): Promise<string | null> => {
      try {
        // Get a token if we don't have one already
        const verificationToken = token || (await executeTurnstile());

        const response = await fetch("/api/register", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            size,
            "cf-turnstile-response": verificationToken,
          }),
        });

        if (!response.ok) {
          throw new Error(
            `Registration failed: ${response.status} ${response.statusText}`
          );
        }

        const data = (await response.json()) as RegisterResponse;

        if (data.success && data.userId) {
          localStorage.setItem("tritan-user-id", data.userId);
          setUserId(data.userId);
          return data.userId;
        } else {
          throw new Error(data.error || "Registration failed");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return null;
      }
    },
    [token]
  );

  // Start verification immediately in background
  useEffect(() => {
    // Only auto-execute if we don't have a userId or token already
    if (!userId && !token && !loading) {
      executeTurnstile().catch(() => {
        // Silently fail for background verification
        // We'll retry when needed
      });
    }
  }, [userId, token, loading]);

  // Cleanup on unmount
  function cleanup() {
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }
    if (containerRef.current && containerRef.current.parentNode) {
      containerRef.current.parentNode.removeChild(containerRef.current);
      containerRef.current = null;
    }
  }

  return {
    executeTurnstile,
    registerUser,
    userId,
    token,
    loading,
    error,
    cleanup,
  };
}
